/**
 * Institution Portal — Scan & Verify (Iteration 21D).
 *
 * Operators paste a citizen's Wallet session-QR token here (or feed
 * it from a hardware scanner acting as a keyboard). The backend
 * endpoint `POST /api/institutions/verify` HMAC-verifies the token,
 * atomically burns it, and returns only the fields the citizen
 * consented to release.
 *
 * Design invariants:
 * - The page never persists the citizen's payload locally. Once the
 *   operator navigates away the last result is gone.
 * - Every response is displayed with its `audit_ref` — that ledger
 *   id is the tamper-evident proof the institution keeps.
 * - Errors surface with the backend's `code` verbatim so operators
 *   can hand it to VeraLok support without translation.
 */
import React, { useMemo, useRef, useState } from "react";
import { api, apiErrorText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  ShieldAlert,
  Lock,
  Copy,
  RefreshCw,
  QrCode,
} from "lucide-react";
import QrCameraScanner from "../components/QrCameraScanner";
import { DemoProvenanceChip, useIsDemoTenant } from "../DemoProvenance";

const CLAIM_LABELS = {
  name: "Full name",
  portrait: "Portrait",
  id_type: "ID type",
  validity: "Validity window",
  verification_level: "Verification level",
  age_18_plus: "18+ signal",
  age_21_plus: "21+ signal",
};

const ERROR_HINTS = {
  malformed_qr: "The scanned value doesn't look like a VeraLok session QR.",
  invalid_signature:
    "The token's signature failed HMAC verification — it wasn't issued by VeraLok.",
  token_expired: "This token has already expired. Ask the citizen to refresh.",
  unknown_token:
    "This token has no matching mint on our side. It may have been revoked.",
  token_already_used: "This token was already burned by an earlier scan.",
  identity_locked:
    "The citizen has locked their VLK_ID. Verification is refused until they unlock.",
  malformed_payload:
    "The token payload couldn't be parsed. Ask the citizen to re-issue.",
  unsupported_token_type:
    "Only Instant Verify tokens are accepted here. Session credentials use a different endpoint.",
  identity_not_found:
    "No identity matches the token's VLK_ID. This is unexpected — please contact VeraLok support.",
};

export default function InstitutionVerify() {
  const [rawInput, setRawInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const canSubmit = rawInput.trim().length >= 8 && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/institutions/verify", {
        qr_data: rawInput.trim(),
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code =
        (typeof detail === "object" && detail?.code) ||
        (typeof detail === "string" ? detail : "unknown");
      setError({
        code,
        message:
          (typeof detail === "object" && detail?.message) ||
          apiErrorText(err),
        status: err?.response?.status,
      });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setRawInput("");
    setResult(null);
    setError(null);
    inputRef.current?.focus();
  };

  return (
    <div
      className="space-y-6 max-w-3xl"
      data-testid="institution-page-verify"
    >
      <DemoProvenanceChip label="DEMO V2 · Scan & Verify — Demo v2 interaction on isolated sample identities" />
      <header className="space-y-1">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <QrCode className="w-5 h-5" /> Scan &amp; Verify
        </h1>
        <p className="text-sm text-muted-foreground">
          Paste (or scan into) a citizen's Wallet session QR. VeraLok
          verifies the signature, burns the token, and returns only the
          claims the citizen consented to release.
        </p>
      </header>

      <DemoSampleQrPanel onToken={(t) => setRawInput(t)} />

      <ScannerCard
        rawInput={rawInput}
        setRawInput={setRawInput}
        onSubmit={submit}
        onReset={reset}
        busy={busy}
        canSubmit={canSubmit}
        inputRef={inputRef}
      />

      {error && <ErrorCard error={error} />}
      {result && <ResultCard result={result} onScanAnother={reset} />}
      {!error && !result && <EmptyState />}
    </div>
  );
}

// ─── Scanner input ───────────────────────────────────────────────────
function ScannerCard({
  rawInput,
  setRawInput,
  onSubmit,
  onReset,
  busy,
  canSubmit,
  inputRef,
}) {
  return (
    <div className="border rounded-xl p-5 bg-card" data-testid="scanner-card">
      <div className="flex items-center justify-between mb-3">
        <label
          htmlFor="qr-input"
          className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          Session QR
        </label>
        <QrCameraScanner
          onScan={(token) => setRawInput(token)}
          testId="verify-qr-camera"
        />
      </div>
      <textarea
        id="qr-input"
        ref={inputRef}
        rows={3}
        value={rawInput}
        onChange={(e) => setRawInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSubmit) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder="vlk://verify?t=…  (or scan with the camera above)"
        className="w-full text-sm font-mono bg-background border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/60"
        data-testid="scanner-input"
      />
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-muted-foreground">
          Scan the citizen's Wallet QR with your camera, or paste the token.
          ⌘/Ctrl + Enter to submit.
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={busy && rawInput.length === 0}
            data-testid="scanner-reset"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            data-testid="scanner-submit"
          >
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Result ──────────────────────────────────────────────────────────
function ResultCard({ result, onScanAnother }) {
  const claims = result.claims || [];
  const identity = result.identity || {};
  const verifiedAt = useMemo(() => {
    if (!result.verified_at) return "—";
    try {
      return new Date(result.verified_at).toLocaleString();
    } catch {
      return result.verified_at;
    }
  }, [result.verified_at]);

  return (
    <div
      className="border rounded-xl overflow-hidden bg-card"
      data-testid="verify-result"
    >
      <div className="flex items-center justify-between px-5 py-3 bg-emerald-500/10 border-b border-emerald-500/30">
        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold">
          <ShieldCheck className="w-5 h-5" />
          Verified
        </div>
        <div className="text-xs text-muted-foreground" data-testid="verify-timestamp">
          {verifiedAt}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
        <Meta label="VLK_ID" value={result.vlk_id} testKey="verify-vlk-id" mono />
        <Meta label="Verify ID" value={result.verify_id} testKey="verify-id" mono />
        <Meta label="Token ID" value={result.token_id} testKey="verify-token-id" mono />
        <MetaCopy
          label="Audit ref"
          value={result.audit_ref}
          testKey="verify-audit-ref"
        />
      </div>

      <div className="px-5 pb-5">
        <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          Consented claims ({claims.length})
        </h3>
        {claims.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            The citizen released no PII fields on this token — only the
            audit signal.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {claims.map((c) => (
              <ClaimRow
                key={c}
                claim={c}
                value={identity[claimField(c)]}
              />
            ))}
          </div>
        )}
      </div>

      <div className="px-5 pb-5 border-t border-border/50 pt-4">
        <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          Assurance metadata
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Meta
            label="Provider"
            value={providerLabel(identity.verification_provider)}
            testKey="verify-provider"
          />
          <Meta
            label="Confidence"
            value={
              identity.verification_confidence != null
                ? `${(identity.verification_confidence * 100).toFixed(1)}%`
                : "—"
            }
            testKey="verify-confidence"
          />
          <Meta
            label="Country"
            value={identity.country || "—"}
            testKey="verify-country"
          />
        </div>
      </div>

      <div className="px-5 pb-5 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={onScanAnother}
          data-testid="verify-scan-another"
        >
          Scan another
        </Button>
      </div>
    </div>
  );
}

function claimField(claim) {
  switch (claim) {
    case "name":
      return "full_name";
    case "portrait":
      return "portrait_url";
    case "id_type":
      return "id_type";
    case "validity":
      return "valid_month_year";
    case "verification_level":
      return "verification_level";
    default:
      return claim;
  }
}

// Customer-facing provider label. The Institution Portal must never
// leak internal provider tokens ("MOCKED", "NULL", "SIM", "VLK_NATIVE",
// etc.) into the operator's screen — those are diagnostic values kept
// verbatim on `identity.verification_provider` for audit / historical
// integrity but presented here through a truthful, user-legible map.
// No provider value is silently reclassified: MOCKED / synthetic
// evidence still surfaces as a "Test Identity", and only genuine
// production-native verification renders as "VeraLok Verified".
const _SYNTHETIC_PROVIDERS = new Set(["MOCKED", "MOCK", "NULL", "SIM", "SIMULATED", "SANDBOX"]);
function providerLabel(raw) {
  if (raw == null || raw === "") return "—";
  const v = String(raw).trim().toUpperCase();
  if (v === "VLK_NATIVE" || v === "VERALOK_NATIVE" || v === "NATIVE") {
    return "VeraLok Verified";
  }
  if (_SYNTHETIC_PROVIDERS.has(v)) return "Test Identity";
  // Any other unknown provider token — do NOT imply verification.
  return "Test Identity";
}

function ClaimRow({ claim, value }) {
  const label = CLAIM_LABELS[claim] || claim;
  const isBool = claim.startsWith("age_");
  const display =
    value == null || value === ""
      ? isBool
        ? "—"
        : "Not released"
      : isBool
      ? "✓"
      : value;
  return (
    <div
      className="rounded-lg border border-border/60 px-3 py-2 bg-muted/30"
      data-testid={`claim-${claim}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="text-sm font-medium break-words">{String(display)}</div>
    </div>
  );
}

function Meta({ label, value, testKey, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </div>
      <div
        className={`text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testKey}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function MetaCopy({ label, value, testKey }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono truncate" data-testid={testKey}>
          {value || "—"}
        </span>
        {value && (
          <button
            type="button"
            onClick={copy}
            className="p-1 rounded hover:bg-muted"
            aria-label="Copy audit ref"
            data-testid={`${testKey}-copy`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
        {copied && (
          <span className="text-[10px] text-emerald-600">copied</span>
        )}
      </div>
    </div>
  );
}

// ─── Error ───────────────────────────────────────────────────────────
function ErrorCard({ error }) {
  const isLocked = error.code === "identity_locked";
  const Icon = isLocked ? Lock : ShieldAlert;
  return (
    <div
      className={`border rounded-xl overflow-hidden bg-card ${
        isLocked
          ? "border-red-500/40"
          : "border-amber-500/40"
      }`}
      data-testid="verify-error"
    >
      <div
        className={`flex items-center gap-2 px-5 py-3 font-semibold ${
          isLocked
            ? "bg-red-500/10 text-red-600 dark:text-red-400"
            : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
        }`}
      >
        <Icon className="w-5 h-5" />
        {isLocked ? "Identity locked" : "Verification refused"}
      </div>
      <div className="p-5 space-y-2 text-sm">
        <div>
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Code
          </span>
          <div className="font-mono" data-testid="verify-error-code">
            {error.code}
            {error.status ? ` · HTTP ${error.status}` : ""}
          </div>
        </div>
        <p className="text-muted-foreground" data-testid="verify-error-message">
          {ERROR_HINTS[error.code] || error.message || "Unable to verify."}
        </p>
      </div>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div
      className="border border-dashed rounded-xl p-8 text-center text-sm text-muted-foreground"
      data-testid="verify-empty"
    >
      Scan or paste a citizen's Wallet session QR to see their consented
      identity claims here.
    </div>
  );
}

// ─── Demo v2 sample-QR helper ─────────────────────────────────────────
// Only appears when the caller is a Demo v2 tenant. Mints a genuine
// Wallet session QR bound to one of the isolated sample identities
// so the operator can complete the full Scan & Verify pathway
// without a paired mobile app. The token flows through the exact
// same `POST /api/institutions/verify` endpoint that production uses.
function DemoSampleQrPanel({ onToken }) {
  const isDemo = useIsDemoTenant();
  const [personas, setPersonas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastToken, setLastToken] = useState(null);

  React.useEffect(() => {
    if (!isDemo) return;
    let alive = true;
    api.get("/demo/citizen/personas")
      .then((r) => alive && setPersonas(r.data?.personas || []))
      .catch(() => {});
    return () => { alive = false; };
  }, [isDemo]);

  if (!isDemo) return null;

  const mint = async (vlk_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo/wallet/session_qr", {
        vlk_id,
        claims: ["name", "id_type", "validity"],
      });
      setLastToken({
        qr_data: data.qr_data,
        participant: data.participant_name,
        expires_at: data.expires_at,
      });
      if (onToken && data.qr_data) onToken(data.qr_data);
    } catch {
      /* surface via existing verify error UI on submit */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-orange-500/40 bg-orange-500/5 rounded-xl p-4 space-y-3"
      data-testid="demo-sample-qr-panel"
    >
      <div className="text-xs uppercase tracking-widest font-bold text-orange-500">
        Demo v2 · Get a sample session QR
      </div>
      <p className="text-xs text-muted-foreground">
        Pick a Demo participant below. VeraLok mints a real, HMAC-signed
        session QR bound to that sample identity and drops it into the
        input. Then click <b>Verify</b> to run the standard pathway.
      </p>
      <div className="flex flex-wrap gap-2">
        {personas.length === 0 && (
          <div className="text-[11px] text-muted-foreground italic">
            Loading Demo participants…
          </div>
        )}
        {personas.map((p) => {
          const first = (p.full_name || "").split(" ")[0]?.toLowerCase();
          return (
            <button
              key={p.vlk_id}
              type="button"
              disabled={busy}
              onClick={() => mint(p.vlk_id)}
              className="text-xs font-mono px-3 py-1.5 rounded-full border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 disabled:opacity-50"
              data-testid={`demo-sample-qr-persona-${first}`}
            >
              <span className="font-semibold">{p.full_name}</span>
              <span className="mx-1 text-orange-500/60">·</span>
              {p.vlk_id}
            </button>
          );
        })}
      </div>
      {lastToken && (
        <div
          className="text-[11px] text-muted-foreground border-t border-orange-500/20 pt-2"
          data-testid="demo-sample-qr-last"
        >
          Ready: sample QR for <b className="text-orange-400">{lastToken.participant}</b> loaded.
          Click <b>Verify</b>. Token expires {new Date(lastToken.expires_at).toLocaleTimeString()}.
        </div>
      )}
    </div>
  );
}
